/**
 * Object Reference Content Component
 *
 * Displays the IDD-based reference documentation for an EnergyPlus object,
 * including description, fields, and metadata.
 *
 * Features:
 * - Field search/filter
 * - Required fields only toggle
 * - Jump to field by index
 * - Distinct badge colors for Autosize vs Autocalc
 * - Expandable field details
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import type { FieldDocumentation } from '../types';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronRightIcon, InfoIcon, SearchIcon, FilterIcon } from './icons';

interface ObjectReferenceContentProps {
  objectType: string;
  memo: string;
  fields: FieldDocumentation[];
  isUnique?: boolean;
  isRequired?: boolean;
  highlightField?: string | undefined;
}

export function ObjectReferenceContent({
  objectType,
  memo,
  fields,
  isUnique = false,
  isRequired = false,
  highlightField,
}: ObjectReferenceContentProps) {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [fieldFilter, setFieldFilter] = useState('');
  const [showRequiredOnly, setShowRequiredOnly] = useState(false);
  const [jumpToIndex, setJumpToIndex] = useState('');
  const highlightRef = useRef<HTMLDivElement>(null);
  const fieldRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Auto-expand and scroll to highlighted field
  useEffect(() => {
    if (!highlightField) return;

    setExpandedFields((prev) => new Set(prev).add(highlightField));
    // Scroll after a brief delay to allow expansion
    const timeoutId = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [highlightField]);

  // Filter fields based on search and required toggle
  const filteredFields = useMemo(() => {
    let result = fields;

    if (showRequiredOnly) {
      result = result.filter((f) => f.required);
    }

    if (fieldFilter.trim()) {
      const filter = fieldFilter.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(filter) ||
          f.id.toLowerCase().includes(filter) ||
          f.memo?.toLowerCase().includes(filter)
      );
    }

    return result;
  }, [fields, fieldFilter, showRequiredOnly]);

  // Get original index for a field
  const getOriginalIndex = (fieldId: string): number => {
    return fields.findIndex((f) => f.id === fieldId);
  };

  const toggleField = (fieldId: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedFields(new Set());
    } else {
      setExpandedFields(new Set(filteredFields.map((f) => f.id)));
    }
    setExpandAll(!expandAll);
  };

  const handleJumpToIndex = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const index = parseInt(jumpToIndex, 10);
      if (index >= 1 && index <= fields.length) {
        const fieldRef = fieldRefs.current.get(index - 1);
        if (fieldRef) {
          fieldRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Expand the field
          const field = fields[index - 1];
          if (field) {
            setExpandedFields((prev) => new Set(prev).add(field.id));
          }
        }
      }
      setJumpToIndex('');
    }
  };

  const getFieldTypeBadge = (field: FieldDocumentation) => {
    const badges: { label: string; className: string }[] = [];

    if (field.required) {
      badges.push({
        label: 'Required',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      });
    }

    // Use different colors for Autosize and Autocalc
    if (field.autosizable) {
      badges.push({
        label: 'Autosize',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      });
    }

    if (field.autocalculatable) {
      badges.push({
        label: 'Autocalc',
        className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
      });
    }

    return badges;
  };

  const formatFieldType = (type: string): string => {
    switch (type) {
      case 'real':
        return 'Number (Real)';
      case 'integer':
        return 'Number (Integer)';
      case 'alpha':
        return 'Text';
      case 'choice':
        return 'Choice';
      case 'object-list':
        return 'Object Reference';
      case 'node':
        return 'Node Name';
      default:
        return type;
    }
  };

  const requiredCount = fields.filter((f) => f.required).length;

  return (
    <div className="space-y-6">
      {/* Object Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-xl font-semibold text-foreground">{objectType}</h2>
          {isUnique && (
            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              Unique
            </span>
          )}
          {isRequired && (
            <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Required
            </span>
          )}
        </div>

        {/* Object description */}
        {memo && (
          <div className="text-sm text-foreground/80 leading-relaxed">
            {memo.split('\n').map((paragraph, i) => (
              <p key={i} className="mb-2 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Fields Section */}
      <div>
        <div className="flex flex-col gap-3 mb-4">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground">
              Fields ({fields.length})
              {requiredCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  {requiredCount} required
                </span>
              )}
            </h3>
            <button
              onClick={handleExpandAll}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {expandAll ? 'Collapse all' : 'Expand all'}
            </button>
          </div>

          {/* Filter controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Search filter */}
            <div className="relative flex-1 min-w-[200px]">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                placeholder="Filter fields..."
                className={cn(
                  'w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input',
                  'bg-background text-foreground placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-1 focus:ring-ring'
                )}
              />
            </div>

            {/* Jump to field */}
            <div className="relative w-24">
              <input
                type="text"
                value={jumpToIndex}
                onChange={(e) => setJumpToIndex(e.target.value.replace(/\D/g, ''))}
                onKeyDown={handleJumpToIndex}
                placeholder={`1-${fields.length}`}
                className={cn(
                  'w-full px-2 py-1.5 text-sm rounded-md border border-input text-center',
                  'bg-background text-foreground placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-1 focus:ring-ring'
                )}
                title="Jump to field number (press Enter)"
              />
            </div>

            {/* Required only toggle */}
            <button
              onClick={() => setShowRequiredOnly(!showRequiredOnly)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors',
                showRequiredOnly
                  ? 'bg-red-100 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-400'
                  : 'bg-background border-input text-muted-foreground hover:text-foreground'
              )}
            >
              <FilterIcon className="h-3.5 w-3.5" />
              Required only
            </button>
          </div>

          {/* Filter status */}
          {(fieldFilter || showRequiredOnly) && (
            <div className="text-xs text-muted-foreground">
              Showing {filteredFields.length} of {fields.length} fields
              {fieldFilter && ` matching "${fieldFilter}"`}
              {showRequiredOnly && ' (required only)'}
              {filteredFields.length !== fields.length && (
                <button
                  onClick={() => {
                    setFieldFilter('');
                    setShowRequiredOnly(false);
                  }}
                  className="ml-2 text-primary hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          {filteredFields.map((field) => {
            const originalIndex = getOriginalIndex(field.id);
            const isExpanded = expandedFields.has(field.id);
            const isHighlighted =
              highlightField &&
              (field.name.toLowerCase() === highlightField.toLowerCase() ||
                field.id.toLowerCase() === highlightField.toLowerCase());
            const badges = getFieldTypeBadge(field);

            return (
              <div
                key={field.id}
                ref={(el) => {
                  if (isHighlighted && el) {
                    (highlightRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  }
                  if (el) {
                    fieldRefs.current.set(originalIndex, el);
                  }
                }}
                className={cn(
                  'border rounded-lg transition-colors',
                  isHighlighted
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                )}
              >
                {/* Field header */}
                <button
                  onClick={() => toggleField(field.id)}
                  className="w-full flex items-center gap-2 p-3 text-left"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}

                  <span className="text-xs text-muted-foreground font-mono shrink-0 w-6">
                    {originalIndex + 1}
                  </span>

                  <span className="font-medium text-sm text-foreground flex-1 truncate">
                    {field.name || field.id}
                  </span>

                  {/* Badges */}
                  <div className="flex gap-1 shrink-0">
                    {badges.map((badge, i) => (
                      <span key={i} className={cn('text-xs px-1.5 py-0.5 rounded', badge.className)}>
                        {badge.label}
                      </span>
                    ))}
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 border-t border-border/50">
                    <div className="grid gap-3 pt-3">
                      {/* Type and Units row */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Type:</span>{' '}
                          <span className="text-foreground">{formatFieldType(field.type)}</span>
                        </div>
                        {field.units && (
                          <div>
                            <span className="text-muted-foreground">Units:</span>{' '}
                            <span className="text-foreground font-medium">{field.units}</span>
                          </div>
                        )}
                        {field.default !== undefined && (
                          <div>
                            <span className="text-muted-foreground">Default:</span>{' '}
                            <span className="text-foreground font-mono">{field.default}</span>
                          </div>
                        )}
                      </div>

                      {/* Range */}
                      {(field.minimum !== undefined || field.maximum !== undefined) && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Range:</span>{' '}
                          <span className="text-foreground font-mono">
                            {field.minimum ?? '-∞'} to {field.maximum ?? '∞'}
                          </span>
                        </div>
                      )}

                      {/* Choices */}
                      {field.choices && field.choices.length > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground block mb-1">Valid choices:</span>
                          <div className="flex flex-wrap gap-1">
                            {field.choices.map((choice) => (
                              <span
                                key={choice}
                                className="px-2 py-0.5 rounded bg-muted text-xs font-mono"
                              >
                                {choice}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Object list reference */}
                      {field.objectList && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">References:</span>{' '}
                          <span className="text-primary font-mono">{field.objectList}</span>
                        </div>
                      )}

                      {/* Description */}
                      {field.memo && (
                        <div className="mt-2 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-300">
                          <div className="flex gap-2">
                            <InfoIcon className="h-4 w-4 shrink-0 mt-0.5" />
                            <div>
                              {field.memo.split('\n').map((line, i) => (
                                <p key={i} className="mb-1 last:mb-0">
                                  {line}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* No results message */}
          {filteredFields.length === 0 && (fieldFilter || showRequiredOnly) && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No fields match your filter criteria.</p>
              <button
                onClick={() => {
                  setFieldFilter('');
                  setShowRequiredOnly(false);
                }}
                className="mt-2 text-primary hover:underline text-sm"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
